/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { FetchUtils } from "abweb/service/utils/rpc/FetchUtils";
import { abModuleRegistry } from "abweb/utils/plugins/ModuleRegistry";
import { DOMUtils } from "abweb/utils/DOMUtils";

/**
 * The type of a manifest generated by webpack.
 * This manifest lists the URLs of the chunks needed to load the module.
 */

/**
 * The URL of a manifest and the (optional) list of entrypoints.
 */

/**
 * A module to dynamically load.  This is either a single URL,
 * list of URLs, or a manifest file to load (which contains the URLs).
 */

/**
 * Order the chunks from a manifest, choosing either a subset of the entrypoints or all entrypoints.
 */
export function orderChunkUrls(manifest, manifestAddress) {
  const entryPoints = manifestAddress.entrypoints ?? Object.keys(manifest.entrypoints) ?? [];
  const chunkUrls = [];
  // make the manifest URL an absolute path
  const manifestUrl = new URL(manifestAddress.manifestUrl, document.URL);
  for (const entryPoint of entryPoints) {
    const js = manifest.entrypoints[entryPoint].assets.js;
    for (const jsFile of js) {
      // chunks are relative to the manifest
      const chunkUrl = new URL(jsFile, manifestUrl).toString();
      chunkUrls.push(chunkUrl);
    }
  }
  return chunkUrls;
}

/**
 * Read a manifest file that lists a set of chunks for a set of entrypoints.
 * These are converted into an array of script URLs, ordered by entrypoint, then by chunk.
 */
async function readModuleManifest(manifestAddress) {
  const manifest = await FetchUtils.fetchAndParseJson(manifestAddress.manifestUrl);
  return orderChunkUrls(manifest, manifestAddress);
}

/**
 * Compute the URLs to load for the given module.  This could be a single URL, array
 * of URLs, or a URL to a manifest.
 */
async function computeChunkUrls(moduleAddress) {
  if (Array.isArray(moduleAddress.address)) {
    return moduleAddress.address;
  } else if (typeof moduleAddress.address === "string") {
    return [moduleAddress.address];
  } else if ("manifestUrl" in moduleAddress.address) {
    return await readModuleManifest(moduleAddress.address);
  } else {
    throw new Error("Unrecognized module address type");
  }
}

/**
 * Load a module, give its entry point URL, URLs, or URL of manifest file.
 * If given a module path as well, assume this path is the path that references the module's exports
 * and return these exports.  The module path can be excluded to load the plugin only for its side effects.
 */
export async function loadModule(moduleAddress) {
  // convert to an ordered set of URLs
  let urls = await computeChunkUrls(moduleAddress);
  // add urls to the DOM, in order, waiting for each to load.
  for (const url of urls) {
    await DOMUtils.loadScript(url);
  }
  // (optionally) retrieve the module loaded from the ModuleRegistry
  return moduleAddress.modulePath ? abModuleRegistry.retrieveModuleIfDefined(moduleAddress.modulePath) : undefined;
}
//# sourceMappingURL=DynamicModuleLoader.js.map
